/**
 * A moment asked for by name. A flow that owns its own timing (the first
 * run's landing) requests a `manual` moment here; the runner opens it on its
 * next tick, ahead of any trigger, and tells the asker how it ended: done
 * (the last card's Done), skip (Skip on any card), or left (the reader went
 * elsewhere mid-walk, the cards spent). Module-level, so a request survives
 * the asker unmounting, and read through useSyncExternalStore.
 */

import { useSyncExternalStore } from "react";

export type CoachClose = "done" | "skip" | "left";

export interface CoachRequest {
  moment: string;
  /** The surface it was asked for; leaving it mid-walk spends the cards. */
  pathname: string;
  /** The cards to walk, by key, in the moment's order; absent means all of them. */
  cards?: readonly string[];
  /** A card has come up; `card` is its index in the walk. */
  onCard?: (card: number, key: string) => void;
  /** What the moment's cards keep off, in place of their own selector. */
  clearSelector?: string;
  /** `card` is the index the walk ended on. */
  onClose: (how: CoachClose, card: number) => void;
}

export interface CoachRequestOptions {
  pathname?: string;
  cards?: readonly string[];
  onCard?: CoachRequest["onCard"];
  clearSelector?: string;
}

let request: CoachRequest | null = null;
const listeners = new Set<() => void>();

function notify() {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function requestCoachMoment(
  moment: string,
  onClose: CoachRequest["onClose"],
  options: CoachRequestOptions = {},
): void {
  request = {
    moment,
    pathname: options.pathname ?? window.location.pathname,
    ...(options.cards ? { cards: options.cards } : {}),
    ...(options.onCard ? { onCard: options.onCard } : {}),
    ...(options.clearSelector ? { clearSelector: options.clearSelector } : {}),
    onClose,
  };
  notify();
}

/** The request is answered: the asker hears how, and the slot is free. */
export function closeCoachRequest(how: CoachClose, card: number): void {
  const current = request;
  if (!current) return;
  request = null;
  notify();
  current.onClose(how, card);
}

export function useCoachRequest(): CoachRequest | null {
  return useSyncExternalStore(subscribe, () => request);
}

export function resetCoachRequestForTest(): void {
  request = null;
}

/**
 * The registry as a request sees it: the requested moment keeps only the
 * cards the request names, in the moment's order, and its cards that keep
 * off a selector keep off the request's instead. A moment with a card
 * count that depends on the run (the landing's, four on a canned run and
 * three on a custom one) stays one moment, stamped once.
 */
export function momentsFor<
  M extends { key: string; cards: { key: string; clearSelector?: string }[] },
>(moments: readonly M[], request: CoachRequest | null): readonly M[] {
  if (!request?.cards && !request?.clearSelector) return moments;
  const keep = request.cards;
  const clear = request.clearSelector;
  return moments.map((m) =>
    m.key === request.moment
      ? {
          ...m,
          cards: m.cards
            .filter((c) => !keep || keep.includes(c.key))
            .map((c) =>
              clear && c.clearSelector ? { ...c, clearSelector: clear } : c,
            ),
        }
      : m,
  );
}
